import { useState, useEffect } from "react";
import { Box, Spinner, Text, Alert, AlertIcon, Select } from "@chakra-ui/react";
import { useAuth } from "../../Contexts/AuthContext";
import { getContracts } from "../../Services/api";
import FileManager from "../../Components/FileManager";
import DebugErrorBoundary from "../../Components/FileManager/DebugErrorBoundary";

const DocumentsPage = () => {
  const [contracts, setContracts] = useState([]);
  const [isLoadingContracts, setIsLoadingContracts] = useState(true);
  const [error, setError] = useState("");
  const [selectedContract, setSelectedContract] = useState(null);

  const { user } = useAuth();

  // [DEBUG] Patch removeChild to catch the source of the "not a child" DOM error
  useEffect(() => {
    const original = Node.prototype.removeChild;
    Node.prototype.removeChild = function debugRemoveChild(child) {
      if (child && child.parentNode !== this) {
        console.group("[Debug:DOM] removeChild MISMATCH ⚠️");
        console.error("Expected parent:", this);
        console.error("Actual parent:", child.parentNode);
        console.error("Child node:", child);
        console.error("Child tagName:", child?.tagName);
        console.error("Child className:", child?.className);
        console.error("Stack trace:", new Error("removeChild trace").stack);
        console.groupEnd();
      }
      return original.call(this, child);
    };
    console.log("[Debug:Documents] removeChild patch installed ✅");
    return () => {
      Node.prototype.removeChild = original;
      console.log("[Debug:Documents] removeChild patch removed");
    };
  }, []);

  useEffect(() => {
    if (!user?.CustomerId) {
      setIsLoadingContracts(false);
      setError("User information is not available.");
      return;
    }
    setIsLoadingContracts(true);
    getContracts(user.CustomerId)
      .then((response) => {
        const contractsData = response.data?.data?.results || [];
        setContracts(contractsData);
        if (contractsData.length > 0) {
          setSelectedContract(contractsData[0]);
        }
      })
      .catch((err) => setError(err.message || "Failed to fetch contracts."))
      .finally(() => setIsLoadingContracts(false));
  }, [user]);

  const handleSelectChange = (event) => {
    const selectedIndex = parseInt(event.target.value, 10);
    setSelectedContract(contracts[selectedIndex]);
  };

  if (isLoadingContracts) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" h="50vh">
        <Box textAlign="center">
          <Spinner size="xl" thickness="3px" color="purple.500" mb={3} />
          <Text color="gray.600">Loading your documents...</Text>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      height="calc(100dvh - 3rem)"
      overflowY={"auto"}
      display="flex"
      flexDirection="column"
    >
      {contracts.length > 0 && (
        <Box p={0.5} borderBottom="1px solid" borderColor="gray.200" bg="white">
          <Select
            onChange={handleSelectChange}
            size="md"
            borderRadius="lg"
            bg="white"
            border="1px solid"
            borderColor="gray.300"
            _focus={{
              borderColor: "purple.500",
              boxShadow: "0 0 0 1px purple.500",
            }}
            maxW="300px"
          >
            {contracts.map((contract, index) => (
              <option key={contract.ContractId} value={index}>
                {contract.ContractTaskName || `Package ${index + 1}`}
              </option>
            ))}
          </Select>
        </Box>
      )}

      <Box flex="1" minH="0">
        {contracts.length > 0 ? (
          selectedContract && (
            // [DEBUG] Wrapped with ErrorBoundary to capture component stack on error
            <DebugErrorBoundary label="FileManager">
              <FileManager
                key={selectedContract.ContractId}
                ContractData={selectedContract}
                Tab="doc"
              />
            </DebugErrorBoundary>
          )
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            h="full"
          >
            <Box textAlign="center">
              <Text fontSize="lg" fontWeight="bold" color="gray.700" mb={2}>
                No Automation Packages
              </Text>
              <Text color="gray.500">
                Your packages will appear here once activated.
              </Text>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DocumentsPage;
